import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Providers } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PhoneNumberOption } from 'src/app/common-components/entity-select/phonenumber/models/phone-number-option';
import { getProviders, providerSearch } from 'src/app/common-components/helpers/provider-helper';
import { ExtensionDetailView } from 'src/app/pages/extension-details/models/extension-detail-view.model';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { PhoneNumberDetailView } from 'src/app/pages/phone-number-details/models/phone-number-detail-view.model';
import { AccountExtensionView } from '../../add-account-extension/models/account-extension-view.model';
import { AccountPhoneNumberView } from '../models/account-phone-number-view.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-account-phone-numbers-view',
    templateUrl: './add-account-phone-numbers-view.component.html',
    standalone: false
})
export class AddAccountPhoneNumbersViewComponent implements OnDestroy, OnInit {
   @Output() formSubmitted = new EventEmitter<any>();

   @Output() formCanceled = new EventEmitter<void>();

   @Input() callState: CallState;

   @Input() form: UntypedFormGroup;

   @Input() existingPhoneNumbers: PhoneNumberDetailView[];

   @Input() existingExtensions: ExtensionDetailView[] | null;

   @Input() isNew: boolean;

   providers = getProviders();

   filteredExtensions: ExtensionDetailView[] | null;

   destroyed$ = new Subject<void>();

   ngOnInit() {
      this.setupPhoneNumberSubscription();

      if (this.existingExtensions) {
         this.filteredExtensions = this.existingExtensions.filter((ext) => ext.type?.displayName !== 'Fax');
      }
   }

   private setupPhoneNumberSubscription(): void {
      this.form
         .get('phoneNumber')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((selectedPhoneNumber: PhoneNumberOption) => {
            if (selectedPhoneNumber && selectedPhoneNumber.displayName !== undefined) {
               this.form.patchValue(
                  {
                     displayName: selectedPhoneNumber.displayName,
                  },
                  { emitEvent: false }
               );
            } else if (selectedPhoneNumber === null) {
               this.form.patchValue(
                  {
                     displayName: '',
                  },
                  { emitEvent: false }
               );
            }
         });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(continuous = false): void {
      if (this.form.valid) {
         const formValue = this.form.value;
         const routeToValue = formValue.routeTo;
         let transformedPhoneNumber: AccountPhoneNumberView;
         if (!this.isNew) {
            const selectedPhoneNumber: PhoneNumberOption = formValue.phoneNumber;
            transformedPhoneNumber = {
               phoneNumber: selectedPhoneNumber.phoneNumber,
               phoneNumberId: selectedPhoneNumber.phoneNumberId,
               displayName: formValue.displayName || null,
               routeTo: routeToValue ? (routeToValue as ExtensionOption) : null,
               datePorted: null,
               isActive: formValue.isActive,
               isPorted: null,
               providerId: null,
            };
         } else {
            transformedPhoneNumber = {
               datePorted: formValue.datePorted,
               displayName: formValue.displayName || null,
               isActive: formValue.isActive,
               isPorted: formValue.isPorted,
               providerId: formValue.providerId,
               phoneNumber: formValue.phoneNumber,
               phoneNumberId: 0,
               routeTo: routeToValue ? (routeToValue as ExtensionOption) : null,
            };
         }

         this.formSubmitted.emit({
            phoneNumber: transformedPhoneNumber,
            continuous,
         });

         if (continuous) {
            this.form.reset({
               phoneNumber: null,
               displayName: '',
               providerId: null,
               isActive: true,
               isPorted: null,
               datePorted: null,
               routeTo: routeToValue,
            });

            Object.keys(this.form.controls).forEach((key) => {
               const control = this.form.get(key);
               if (control) {
                  control.markAsUntouched();
                  control.markAsPristine();
               }
            });

            this.form.markAsPristine();
         }
      } else {
         this.form.markAllAsTouched();
      }
   }

   extensionDisplayWith = (option: AccountExtensionView): string => {
      if (!option) return '';
      return `${option.number} - ${option.name} (${option.type.displayName})`;
   };

   providerDisplayWith(option: Providers): string {
      return `${option ?? ''}`;
   }

   providerSearch(searchText: string) {
      this.providers = providerSearch(searchText);
   }

   providerReset() {
      this.providers = getProviders();
   }
}
